<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { Eye } from "@iconoir/vue";
import type { Order, Workspace } from "@artech-global/types";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import { useList } from "~/composables/use-list";
import { useFormat } from "~/composables/use-format";
import { AdminPermission } from "~~/schemas/admin-role";
import OrderDetailModal from "./OrderDetailModal.vue";

defineOptions({ name: "OrderList" });

const props = defineProps<{
  workspaceID?: string;
  embedded?: boolean;
}>();

const { t } = useI18n();
const { d, c } = useFormat();
const { can } = useAccess();
const { $api } = useNuxtApp();
const toast = useToast();

const showWorkspaceFilter = computed(() => !props.embedded && !props.workspaceID);
const showWorkspaceColumn = computed(() => !props.embedded && !props.workspaceID);
const showDateRange = computed(() => !props.embedded);

interface OrderRow extends Order {
  paymentStatus?: string;
  currentTotalPrice?: number;
  paidAmount?: number;
  dueAmount?: number;
  refundedAmount?: number;
}

interface OrderFilter {
  workspaceID: string;
  status: string;
  keyword: string;
  from: string;
  to: string;
  page: number;
  pageSize: number;
}

const defaultFilter = (): OrderFilter => ({
  workspaceID: props.workspaceID || "",
  status: "",
  keyword: "",
  from: "",
  to: "",
  page: 1,
  pageSize: 20,
});

const {
  firstLoading,
  listLoading,
  items,
  itemCount,
  pageCount,
  filter,
  isEmpty,
  isSearchEmpty,
  load,
  search,
  searchDebounced,
} = useList<OrderRow, OrderFilter>({
  endpoint: "/orders",
  defaultFilter,
});

const fromDate = computed<number>({
  get: () => (filter.from ? Number(filter.from) : 0),
  set: (val) => {
    filter.from = val > 0 ? String(val) : "";
  },
});

const toDate = computed<number>({
  get: () => (filter.to ? Number(filter.to) : 0),
  set: (val) => {
    filter.to = val > 0 ? String(val) : "";
  },
});

const workspaces = ref<Workspace[]>([]);

const workspaceOptions = computed(() => [
  { value: "", label: t("order.filterWorkspacePlaceholder") },
  ...workspaces.value.map((w) => ({ value: String(w.id), label: w.name })),
]);

const statusOptions = computed(() => [
  { value: "", label: t("order.statusAll") },
  { value: "pending", label: t("order.statusPending") },
  { value: "confirmed", label: t("order.statusConfirmed") },
  { value: "cancelled", label: t("order.statusCancelled") },
  { value: "open", label: t("order.statusOpen") },
  { value: "paymentSuccess", label: t("order.statusPaymentSuccess") },
]);

const columns = computed(() => {
  const cols: any[] = [
    { key: "code", label: t("order.columnCode"), class: "min-w-[112px]" },
    { key: "buyer", label: t("order.columnBuyer"), class: "md:min-w-[240px]" },
    { key: "event", label: t("order.columnEvent"), class: "min-w-[160px] md:table-cell" },
  ];

  if (showWorkspaceColumn.value) {
    cols.push({
      key: "workspace",
      label: t("order.columnWorkspace"),
      class: "md:min-w-[240px]",
    });
  }

  cols.push(
    { key: "total", label: t("order.columnTotal"), align: "right", class: "min-w-[112px]" },
    { key: "status", label: t("order.columnStatus"), class: "min-w-[112px]" },
    {
      key: "createTime",
      label: t("order.columnCreateTime"),
      class: "min-w-[160px] md:table-cell",
    },
    { key: "actions", align: "right", label: t("ui.action"), class: "min-w-[80px]" },
  );

  return cols;
});

const modalRef = ref<InstanceType<typeof OrderDetailModal> | null>(null);

function openDetail(row: OrderRow) {
  modalRef.value?.open(row);
}

function statusIntent(status: string) {
  switch (status) {
    case "confirmed":
      return "positive";
    case "cancelled":
      return "negative";
    case "pending":
      return "warning";
    case "open":
      return "informative";
    default:
      return "neutral";
  }
}

function statusLabel(status: string) {
  if (!status) return "—";
  const key = `order.status${status.charAt(0).toUpperCase() + status.slice(1)}`;
  return t(key);
}

async function loadWorkspaces() {
  if (!showWorkspaceFilter.value) return;
  try {
    const res: any = await $api("/workspaces", {
      method: "GET",
      query: { page: 1, pageSize: 200 },
    });
    workspaces.value = (res?.items as Workspace[]) || [];
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("order.loadWorkspacesError") });
  }
}

watch(
  () => props.workspaceID,
  (val) => {
    filter.workspaceID = val || "";
    search();
  },
);

watch(
  () => filter.workspaceID,
  () => {
    if (props.workspaceID) return;
    search();
  },
);

watch(
  () => filter.status,
  () => search(),
);

watch(
  () => filter.keyword,
  () => searchDebounced(),
);

watch(
  () => [filter.from, filter.to],
  () => search(),
);

watch(
  () => filter.page,
  () => load(),
);

onMounted(async () => {
  await Promise.all([loadWorkspaces(), load()]);
});
</script>

<template>
  <ListPage
    :title="embedded ? undefined : t('order.title')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('order.itemsSuffix')}` }}
    </template>

    <template #filter-left>
      <FormSelect
        v-if="showWorkspaceFilter"
        v-model="filter.workspaceID"
        :options="workspaceOptions"
        size="sm"
        class="w-full md:flex-1"
      />
      <FormSelect
        v-model="filter.status"
        :options="statusOptions"
        size="sm"
        class="w-full md:flex-1"
      />
    </template>

    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('order.searchPlaceholder')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #empty>
      <ListEmpty
        :title="t('order.emptyTitle')"
        :subtitle="t('order.emptySubtitle')"
      />
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('order.emptySearchTitle')"
        :subtitle="t('order.emptySearchSubtitle')"
      />
    </template>

    <template #default>
      <DataTable :columns="columns" :items="items">
        <template #cell-code="{ row }">
          <span class="font-semibold">{{ row.code }}</span>
        </template>

        <template #cell-buyer="{ row }">
          <div class="flex flex-col min-w-0">
            <span class="font-semibold">{{ row.buyerName || "—" }}</span>
            <span class="text-body-small-tertiary content-neutral-medium">
              {{ row.buyerEmail }}
            </span>
          </div>
        </template>

        <template #cell-event="{ row }">
          <span v-if="row.event?.name">{{ row.event.name }}</span>
          <span v-else class="content-neutral-medium">—</span>
        </template>

        <template #cell-workspace="{ row }">
          <span>{{ row.workspace?.name || "—" }}</span>
        </template>

        <template #cell-total="{ row }">
          {{ c(row.currentTotalPrice ?? row.totalPrice, row.currency) }}
        </template>

        <template #cell-status="{ row }">
          <Badge :intent="statusIntent(row.status)" emphasis="medium" size="small">
            {{ statusLabel(row.status) }}
          </Badge>
        </template>

        <template #cell-createTime="{ row }">
          {{ d(row.createTime, 'datetime') }}
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              v-if="can(AdminPermission.OrdersRead)"
              variant="neutral-flat"
              size="small"
              :title="t('action.view')"
              :aria-label="t('action.view')"
              @click.stop="openDetail(row)"
            >
              <Eye class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        class="mt-medium"
      />
    </template>
  </ListPage>

  <OrderDetailModal ref="modalRef" />
</template>

